import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withKeys as _withKeys, isRef as _isRef, vModelText as _vModelText, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "flex min-h-0 min-w-0 max-w-full flex-col gap-4 p-4 md:p-6" }
const _hoisted_2 = {
  key: 0,
  role: "alert",
  class: "flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
}
const _hoisted_3 = { class: "mr-auto" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 1,
  role: "status",
  class: "rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
}
const _hoisted_6 = {
  key: 2,
  role: "alert",
  class: "flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
}
const _hoisted_7 = {
  class: "min-w-0 overflow-hidden rounded-2xl border border-[#e2e8f1] bg-white shadow-sm",
  "aria-label": "Renewal accounts"
}
const _hoisted_8 = { class: "flex flex-wrap items-center gap-3 p-5 pb-4" }
const _hoisted_9 = { class: "mr-auto" }
const _hoisted_10 = { class: "text-xs text-[#758299]" }
const _hoisted_11 = { class: "flex items-center gap-2 text-xs font-semibold text-[#65758d]" }
const _hoisted_12 = ["aria-busy"]
const _hoisted_13 = ["disabled", "aria-expanded", "onKeydown"]
const _hoisted_14 = {
  key: 0,
  id: "renewal-export-options",
  class: "absolute inset-x-0 top-full z-40 pt-1",
  role: "group",
  "aria-label": "Export formats"
}
const _hoisted_15 = { class: "rounded-lg border border-[#dce4ee] bg-white p-1 shadow-lg" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = {
  key: 0,
  role: "alert",
  class: "mx-5 mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
}
const _hoisted_19 = { class: "grid gap-2 border-b border-[#e8edf4] bg-[#fbfcff] p-4 lg:grid-cols-[minmax(220px,1fr)_180px_200px_190px]" }
const _hoisted_20 = ["value"]
const _hoisted_21 = ["aria-busy"]
const _hoisted_22 = { class: "renewal-table w-full border-separate border-spacing-0 text-left text-xs" }
const _hoisted_23 = { class: "sticky top-0 z-30 bg-[#f8fafd] text-[11px] uppercase tracking-wide text-[#657791]" }
const _hoisted_24 = { key: 0 }
const _hoisted_25 = { key: 1 }
const _hoisted_26 = {
  colspan: "9",
  class: "h-32 text-center text-[#758299]"
}
const _hoisted_27 = ["data-account-id"]
const _hoisted_28 = { class: "account-column" }
const _hoisted_29 = { class: "font-bold text-[#173352]" }
const _hoisted_30 = { class: "mt-1 break-all text-[11px] text-[#758299]" }
const _hoisted_31 = ["data-field"]
const _hoisted_32 = { "data-field": "clientAccountStatus" }
const _hoisted_33 = { class: "actions-column" }
const _hoisted_34 = { class: "flex flex-col items-start gap-2" }
const _hoisted_35 = ["disabled", "aria-label", "onClick"]
const _hoisted_36 = { class: "flex flex-wrap items-center justify-between gap-3 border-t border-[#e8edf4] px-5 py-4 text-xs text-[#65758d]" }
const _hoisted_37 = { "aria-live": "polite" }
const _hoisted_38 = {
  key: 0,
  class: "flex items-center gap-3"
}
const _hoisted_39 = ["disabled"]
const _hoisted_40 = ["disabled"]

import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { onClickOutside } from "@vueuse/core";
import RenewalAccountDetails from "@/components/dashboard/renewals/RenewalAccountDetails.vue";
import RenewalKpiCards from "@/components/dashboard/renewals/RenewalKpiCards.vue";
import RenewalEditModal from "@/components/dashboard/renewals/RenewalEditModal.vue";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useClientsQuery, type NormalizedClient } from "@/composables/useClientsQuery";
import { useRenewalTracking } from "@/composables/useRenewalTracking";
import { getRenewalKpiCounts, type RenewalKpiCounts } from "@/utils/renewalMetrics";
import type { ClientAccountStatus } from "@/services/clientService";
import { useAuthStore } from "@/store/auth";


export default /*@__PURE__*/_defineComponent({
  __name: 'RenewalManagementView',
  setup(__props) {

const router = useRouter();
const queryClient = useQueryClient();
const authStore = useAuthStore();
const canEditRenewalTracking = computed(() => authStore.canEditRenewalTracking);
const { searchQuery, normalizedClients, isLoading, isError, error } = useClientsQuery();
// Match the account selection used by the DMP Accounts page.
const dmpClients = computed(() => normalizedClients.value.filter(
  (client) => client.servicePlanCode === "dmp"
));
const statusFilter = ref("");
const attentionFilter = ref<keyof RenewalKpiCounts | "">("");
const sortBy = ref("account");
const pageSize = ref<5 | 10 | 20 | "all">(10);
const currentPage = ref(1);
const accountListScroll = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);

const dateColumns = [
  { label: "Onboarding date", field: "onboardingDateValue" },
  { label: "Subscription start", field: "subscriptionStartDateValue" },
  { label: "Renewal date", field: "renewalDateValue" },
  { label: "Payment due date", field: "paymentDueDateValue" },
  { label: "Renewal start", field: "renewalStartDateValue" },
  { label: "Renewal end", field: "renewalEndDateValue" },
] as const;
const attentionOrder: (keyof RenewalKpiCounts)[] = [
  "overdueRenewals", "expiredAccounts", "dueThisMonth",
  "renewalsNextMonth", "recentlyRenewed",
];

const renewalKpiCounts = computed(() => getRenewalKpiCounts(dmpClients.value));
// Use the KPI calculator for each account so filters share its existing rules.
const accountRows = computed(() => dmpClients.value.map((client) => ({
  client,
  attention: getRenewalKpiCounts([client]),
})));
const accountStatusOptions: readonly ClientAccountStatus[] = [
  "For Activation",
  "Active",
  "On Hold",
  "Offboarding",
  "Offboarded",
  "Reactivation Onboarding",
];

function compareDates(left: string, right: string) {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  const leftMissing = Number.isNaN(leftTime);
  const rightMissing = Number.isNaN(rightTime);
  if (leftMissing || rightMissing) return Number(leftMissing) - Number(rightMissing);
  return leftTime - rightTime;
}

function attentionPriority(counts: RenewalKpiCounts) {
  const index = attentionOrder.findIndex((key) => counts[key] > 0);
  return index === -1 ? attentionOrder.length : index;
}

const visibleRows = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  return accountRows.value.filter(({ client, attention }) => {
    const matchesSearch = !query || [client.companyName, client.referenceCode, client.email]
      .join(" ").toLowerCase().includes(query);
    const matchesStatus = !statusFilter.value || client.clientAccountStatus === statusFilter.value;
    return matchesSearch
      && matchesStatus
      && (!attentionFilter.value || attention[attentionFilter.value] > 0);
  }).sort((left, right) => {
    const byName = left.client.companyName.localeCompare(right.client.companyName, undefined, {
      sensitivity: "base",
    });
    if (sortBy.value === "attention") {
      return attentionPriority(left.attention) - attentionPriority(right.attention)
        || compareDates(left.client.paymentDueDateValue, right.client.paymentDueDateValue)
        || byName;
    }
    if (sortBy.value === "payment" || sortBy.value === "renewal") {
      const field = sortBy.value === "payment" ? "paymentDueDateValue" : "renewalDateValue";
      return compareDates(left.client[field], right.client[field]) || byName;
    }
    return byName;
  });
});
const pageCount = computed(() => pageSize.value === "all"
  ? 1 : Math.max(1, Math.ceil(visibleRows.value.length / pageSize.value)));
const paginatedRows = computed(() => {
  if (pageSize.value === "all") return visibleRows.value;
  const start = (currentPage.value - 1) * pageSize.value;
  return visibleRows.value.slice(start, start + pageSize.value);
});
const firstVisibleRow = computed(() => !visibleRows.value.length ? 0
  : pageSize.value === "all" ? 1 : (currentPage.value - 1) * pageSize.value + 1);
const lastVisibleRow = computed(() => pageSize.value === "all" ? visibleRows.value.length
  : Math.min(currentPage.value * pageSize.value, visibleRows.value.length));
const isExporting = ref(false);
const exportError = ref("");
const exportDisabled = computed(() => isLoading.value || isError.value
  || isExporting.value || !paginatedRows.value.length);
const isExportMenuOpen = ref(false);
const exportMenu = ref<HTMLElement | null>(null);
const exportTrigger = ref<HTMLButtonElement | null>(null);
let exportMenuPinned = false;

function closeExportMenu(restoreFocus = false) {
  isExportMenuOpen.value = false;
  exportMenuPinned = false;
  if (restoreFocus) exportTrigger.value?.focus();
}

function openExportMenu() {
  if (!exportDisabled.value) isExportMenuOpen.value = true;
}

function toggleExportMenu() {
  if (exportDisabled.value) return;
  if (isExportMenuOpen.value && exportMenuPinned) closeExportMenu();
  else {
    isExportMenuOpen.value = true;
    exportMenuPinned = true;
  }
}

function leaveExportMenu() {
  if (!exportMenuPinned && !exportMenu.value?.contains(document.activeElement)) closeExportMenu();
}

function onExportFocusOut(event: FocusEvent) {
  if (!(event.relatedTarget instanceof Node) || !exportMenu.value?.contains(event.relatedTarget)) {
    closeExportMenu();
  }
}

async function focusExportChoice() {
  if (exportDisabled.value) return;
  isExportMenuOpen.value = true;
  exportMenuPinned = true;
  await nextTick();
  exportMenu.value?.querySelector<HTMLButtonElement>("[data-export-format]")?.focus();
}

onClickOutside(exportMenu, () => closeExportMenu());
watch(exportDisabled, (disabled) => { if (disabled) closeExportMenu(); });

watch([searchQuery, statusFilter, attentionFilter, sortBy, pageSize], () => {
  currentPage.value = 1;
});
watch(pageCount, (count) => {
  if (currentPage.value > count) currentPage.value = count;
});
watch([currentPage, searchQuery, statusFilter, attentionFilter, sortBy, pageSize], () => {
  if (accountListScroll.value) accountListScroll.value.scrollTop = 0;
});

function applyKpiFilter(filter: keyof RenewalKpiCounts) {
  searchQuery.value = "";
  statusFilter.value = "";
  attentionFilter.value = filter;
  currentPage.value = 1;
}

function formatDate(value: string) {
  if (!value) return "—";
  const parsed = /^\d{4}-\d{2}-\d{2}/.test(value)
    ? new Date(value.slice(0, 10) + "T00:00:00") : new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function displayEmail(client: NormalizedClient) {
  return client.email && client.email !== "No email provided" ? client.email : "—";
}

function exportDate(value: string) {
  const iso = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [year, month, day] = iso.split("-").map(Number);
  // Use calendar components for day arithmetic, never local-time date parsing.
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day) return null;
  const days = (timestamp - Date.UTC(1899, 11, 31)) / 86400000;
  // Excel's 1900 calendar includes a fictional February 29.
  return { iso, serial: days + (timestamp >= Date.UTC(1900, 2, 1) ? 1 : 0) };
}

function downloadExport(blob: Blob, filename: string) {
  if (unmounted) return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  try {
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    // Let the browser start the download before releasing the object URL.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

async function exportVisibleAccounts(format: "csv" | "xlsx") {
  if (exportDisabled.value || unmounted) return;
  closeExportMenu(true);
  // Snapshot the displayed page before loading the export library. Later table
  // changes must not change the rows selected by this click.
  const headers = ["Account", "Email", ...dateColumns.map((column) => column.label), "Account status"];
  const rows = paginatedRows.value.map(({ client }) => [
    client.companyName || "—",
    displayEmail(client),
    ...dateColumns.map((column) => exportDate(client[column.field])),
    client.clientAccountStatus,
  ]);
  const today = new Date();
  const date = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0")].join("-");
  const filename = "renewal-dmp-accounts-" + date + "-rows-"
    + firstVisibleRow.value + "-" + lastVisibleRow.value + "." + format;
  isExporting.value = true;
  exportError.value = "";
  try {
    if (format === "csv") {
      const csvRows = [headers, ...rows].map((row) => row.map((value) => {
        let text = typeof value === "string" ? value : value?.iso || "—";
        if (/^\s*[=+\-@]/.test(text)) text = "'" + text;
        return '"' + text.replace(/"/g, '""') + '"';
      }).join(","));
      downloadExport(new Blob(["\uFEFF" + csvRows.join("\r\n")], {
        type: "text/csv;charset=utf-8;",
      }), filename);
      return;
    }
    const XLSX = await import("xlsx");
    if (unmounted) return;
    const data = rows.map((row) => row.map((value) => typeof value === "string" ? value
      : value ? { t: "n", v: value.serial, z: "mmm d, yyyy" } : "—"));
    const sheet = XLSX.utils.aoa_to_sheet([["DMP accounts " + date], headers, ...data]);
    sheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
    sheet["!autofilter"] = { ref: XLSX.utils.encode_range({
      s: { r: 1, c: 0 }, e: { r: rows.length + 1, c: headers.length - 1 },
    }) };
    sheet["!cols"] = [{ wch: 32 }, { wch: 36 }, ...dateColumns.map(() => ({ wch: 20 })), { wch: 26 }];
    sheet["!rows"] = [{ hpt: 30 }, { hpt: 28 }, ...rows.map(() => ({ hpt: 22 }))];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Renewal");
    // CE ignores custom cell fonts. Use its bundled ZIP support to add the two
    // font styles directly to this generated workbook, preserving date styles.
    const zip = XLSX.CFB.read(new Uint8Array(XLSX.write(workbook, { bookType: "xlsx", type: "array" })), { type: "array" });
    const namespace = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
    const readXml = (path: string): XMLDocument => {
      // CFB paths containing a slash must start at the archive root.
      const entry = XLSX.CFB.find(zip, "/" + path);
      if (!entry) throw new Error("Missing workbook XML: " + path);
      const xml = new DOMParser().parseFromString(new TextDecoder().decode(entry.content), "application/xml");
      if (xml.getElementsByTagName("parsererror").length) throw new Error("Invalid workbook XML");
      return xml;
    };
    const saveXml = (path: string, xml: XMLDocument) => XLSX.CFB.utils.cfb_add(
      zip, "/" + path, new TextEncoder().encode(new XMLSerializer().serializeToString(xml))
    );
    const styles = readXml("xl/styles.xml");
    const fonts = styles.getElementsByTagNameNS(namespace, "fonts")[0];
    const cellStyles = styles.getElementsByTagNameNS(namespace, "cellXfs")[0];
    const baseFont = fonts?.firstElementChild;
    const baseStyle = cellStyles?.firstElementChild;
    if (!baseFont || !baseStyle) throw new Error("Missing workbook styles");
    const addBoldStyle = (size?: number) => {
      const font = baseFont.cloneNode(true) as Element;
      font.appendChild(styles.createElementNS(namespace, "b"));
      if (size) font.getElementsByTagNameNS(namespace, "sz")[0].setAttribute("val", String(size));
      const style = baseStyle.cloneNode(true) as Element;
      style.setAttribute("fontId", String(fonts.children.length));
      style.setAttribute("applyFont", "1");
      const index = cellStyles.children.length;
      fonts.appendChild(font);
      fonts.setAttribute("count", String(fonts.children.length));
      cellStyles.appendChild(style);
      cellStyles.setAttribute("count", String(cellStyles.children.length));
      return String(index);
    };
    const titleStyle = addBoldStyle(16);
    const headerStyle = addBoldStyle();
    const worksheet = readXml("xl/worksheets/sheet1.xml");
    Array.from(worksheet.getElementsByTagNameNS(namespace, "c")).forEach((cell) => {
      const reference = cell.getAttribute("r") || "";
      if (reference === "A1") cell.setAttribute("s", titleStyle);
      else if (/^[A-Z]+2$/.test(reference)) cell.setAttribute("s", headerStyle);
    });
    saveXml("xl/styles.xml", styles);
    saveXml("xl/worksheets/sheet1.xml", worksheet);
    const bytes = XLSX.CFB.write(zip, { fileType: "zip", type: "array", compression: true });
    downloadExport(new Blob([new Uint8Array(bytes)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }), filename);
  } catch {
    if (!unmounted) exportError.value = "Unable to export these accounts. Please try again.";
  } finally {
    if (!unmounted) isExporting.value = false;
  }
}

function statusClass(status: string) {
  if (status === "Offboarded") return "border-slate-200 bg-slate-100 text-slate-700";
  if (status === "Offboarding") return "border-rose-200 bg-rose-50 text-rose-700";
  if (status === "On Hold") return "border-amber-200 bg-amber-50 text-amber-800";
  if (status === "Grace Period") return "border-orange-200 bg-orange-50 text-orange-800";
  if (status === "For Activation") return "border-sky-200 bg-sky-50 text-sky-800";
  if (status === "Reactivation Onboarding") return "border-purple-200 bg-purple-50 text-purple-800";
  return "border-emerald-200 bg-emerald-50 text-emerald-800";
}

// Keep selection independent of table filters and fetch details only for a row
// action. The shared tracking composable and its other callers stay unchanged.
const activeClientId = ref<NormalizedClient["id"] | null>(null);
const activeClients = computed(() => dmpClients.value.filter(
  (client) => activeClientId.value !== null && String(client.id) === String(activeClientId.value)
));
const {
  selectedClient, selectedClientDetail, detailIsLoading, detailIsError, refetchDetails,
} = useRenewalTracking(activeClients);
const selectedAccountStatus = computed(() => {
  if (!selectedClient.value) return undefined;
  const label = selectedClientDetail.value?.client_account_status
    ?? selectedClient.value.clientAccountStatus;
  return { label, badgeClass: statusClass(label) };
});
const showDetailsModal = ref(false);
const showEditModal = ref(false);
const editingClient = ref<NormalizedClient | null>(null);
const editingNote = ref<string | null>(null);
const editingAccountStatus = ref("");
const preparingEdit = ref(false);
const editLoadError = ref(false);
const retryingAccounts = ref(false);
let editRequest = 0;
let unmounted = false;
let returnFocusTo: HTMLElement | null = null;
let focusTimer: ReturnType<typeof setTimeout> | undefined;

function rememberFocus(event?: Event) {
  if (focusTimer !== undefined) clearTimeout(focusTimer);
  if (event?.currentTarget instanceof HTMLElement) returnFocusTo = event.currentTarget;
}

async function restoreRowFocus() {
  await nextTick();
  if (unmounted) return;
  if (focusTimer !== undefined) clearTimeout(focusTimer);
  // Run after the existing edit dialog's own focus-scope cleanup.
  focusTimer = setTimeout(() => {
    if (showDetailsModal.value || showEditModal.value || preparingEdit.value) return;
    const target = returnFocusTo?.isConnected ? returnFocusTo : searchInput.value;
    target?.focus({ preventScroll: true });
  }, 0);
}

function closeDetailsFocus(event: Event) {
  event.preventDefault();
  void restoreRowFocus();
}

function openDetails(client: NormalizedClient, event: Event) {
  if (preparingEdit.value) return;
  rememberFocus(event);
  editLoadError.value = false;
  activeClientId.value = client.id;
  showDetailsModal.value = true;
}

async function openEditModal(client: NormalizedClient | null, event?: Event) {
  if (!canEditRenewalTracking.value || !client || preparingEdit.value) return;
  rememberFocus(event);
  const request = ++editRequest;
  activeClientId.value = client.id;
  preparingEdit.value = true;
  editLoadError.value = false;
  showDetailsModal.value = false;
  try {
    await nextTick();
    if (request !== editRequest) return;
    const result = await refetchDetails();
    if (request !== editRequest || !canEditRenewalTracking.value) return;
    if (result.isError || !result.data) throw new Error("Account details unavailable");
    editingClient.value = activeClients.value[0] ?? null;
    if (!editingClient.value) throw new Error("Account no longer available");
    // Snapshot loaded fields so background refreshes cannot overwrite typing.
    editingNote.value = result.data.renewal_note ?? null;
    editingAccountStatus.value = result.data.client_account_status
      ?? editingClient.value.raw?.client_account_status
      ?? editingClient.value.clientAccountStatus;
    showEditModal.value = true;
  } catch {
    if (request === editRequest) editLoadError.value = true;
  } finally {
    if (request === editRequest) {
      preparingEdit.value = false;
      if (!showEditModal.value) void restoreRowFocus();
    }
  }
}

watch(showEditModal, (isOpen, wasOpen) => {
  if (!isOpen && wasOpen) {
    editingClient.value = null;
    void refetchDetails();
    void restoreRowFocus();
  }
});
watch(canEditRenewalTracking, (canEdit) => {
  if (!canEdit) {
    ++editRequest;
    preparingEdit.value = false;
    showEditModal.value = false;
    editLoadError.value = false;
    void restoreRowFocus();
  }
});
onBeforeUnmount(() => {
  unmounted = true;
  ++editRequest;
  if (focusTimer !== undefined) clearTimeout(focusTimer);
});

function openFullProfile() {
  const routeKey = selectedClient.value?.raw?.route_key;
  if (!routeKey) return;
  showDetailsModal.value = false;
  void router.push({ name: "ClientProfile", params: { id: routeKey } });
}

async function retryAccounts() {
  retryingAccounts.value = true;
  try {
    await queryClient.refetchQueries({ queryKey: ["clients"], exact: true });
  } finally {
    retryingAccounts.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _cache[30] || (_cache[30] = _createElementVNode("header", null, [
      _createElementVNode("p", { class: "text-xs font-bold uppercase tracking-[0.12em] text-indigo-500" }, " Account Profiles "),
      _createElementVNode("h1", { class: "mt-1 text-2xl font-bold tracking-tight text-slate-800" }, " DMP Accounts "),
      _createElementVNode("p", { class: "mt-1 max-w-2xl text-sm text-slate-500" }, " Review every DMP account's renewal timeline and open the details that need attention. ")
    ], -1)),
    _createVNode(RenewalKpiCards, {
      counts: renewalKpiCounts.value,
      interactive: "",
      "active-filter": attentionFilter.value,
      onSelectFilter: applyKpiFilter
    }, null, 8, ["counts", "active-filter"]),
    (_unref(isError))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[15] || (_cache[15] = _createElementVNode("p", { class: "font-semibold" }, " Unable to load renewal accounts. ", -1)),
            _createElementVNode("p", null, _toDisplayString(_unref(error)?.message || "Please try again later."), 1)
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "renewal-button",
            disabled: retryingAccounts.value,
            onClick: retryAccounts
          }, _toDisplayString(retryingAccounts.value ? "Retrying…" : "Retry"), 9, _hoisted_4)
        ]))
      : _createCommentVNode("", true),
    (preparingEdit.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Loading renewal details for " + _toDisplayString(_unref(selectedClient)?.companyName || "the selected account") + "… ", 1))
      : _createCommentVNode("", true),
    (editLoadError.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _cache[16] || (_cache[16] = _createElementVNode("span", { class: "mr-auto" }, "Unable to load this account's renewal details. Please retry before editing.", -1)),
          _createElementVNode("button", {
            type: "button",
            class: "renewal-button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (openEditModal(_unref(selectedClient))))
          }, " Retry edit ")
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _cache[17] || (_cache[17] = _createElementVNode("h2", { class: "text-lg font-bold text-[#173352]" }, " DMP accounts ", -1)),
          _createElementVNode("p", _hoisted_10, _toDisplayString(visibleRows.value.length) + " matching accounts · " + _toDisplayString(dmpClients.value.length) + " total DMP accounts ", 1)
        ]),
        _createElementVNode("label", _hoisted_11, [
          _cache[19] || (_cache[19] = _createTextVNode(" Show ", -1)),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pageSize).value = $event)),
            class: "renewal-control h-9",
            "aria-label": "Accounts per page"
          }, [...(_cache[18] || (_cache[18] = [
            _createElementVNode("option", { value: 5 }, "5", -1),
            _createElementVNode("option", { value: 10 }, "10", -1),
            _createElementVNode("option", { value: 20 }, "20", -1),
            _createElementVNode("option", { value: "all" }, "All", -1)
          ]))], 512), [
            [_vModelSelect, pageSize.value]
          ]),
          _cache[20] || (_cache[20] = _createTextVNode(" accounts ", -1))
        ]),
        _createElementVNode("div", {
          ref_key: "exportMenu",
          ref: exportMenu,
          class: "relative w-36 shrink-0",
          "aria-busy": isExporting.value,
          onMouseenter: openExportMenu,
          onMouseleave: leaveExportMenu,
          onFocusout: onExportFocusOut,
          onKeydown: _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event: any) => (closeExportMenu(true)), ["prevent"]), ["esc"]))
        }, [
          _createElementVNode("button", {
            ref_key: "exportTrigger",
            ref: exportTrigger,
            type: "button",
            class: "renewal-button w-full",
            disabled: exportDisabled.value,
            "aria-expanded": isExportMenuOpen.value,
            "aria-controls": "renewal-export-options",
            title: "Export only the rows currently shown on this page",
            onClick: toggleExportMenu,
            onKeydown: _withKeys(_withModifiers(focusExportChoice, ["prevent"]), ["down"])
          }, [...(_cache[21] || (_cache[21] = [
            _createTextVNode(" Export ", -1),
            _createElementVNode("span", {
              "aria-hidden": "true",
              class: "ml-2 inline-block border-x-[4px] border-t-[5px] border-x-transparent border-t-current"
            }, null, -1)
          ]))], 40, _hoisted_13),
          (isExportMenuOpen.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("button", {
                    type: "button",
                    "data-export-format": "csv",
                    class: "renewal-button block w-full border-0 text-left",
                    disabled: exportDisabled.value,
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (exportVisibleAccounts('csv')))
                  }, " CSV ", 8, _hoisted_16),
                  _createElementVNode("button", {
                    type: "button",
                    "data-export-format": "xlsx",
                    class: "renewal-button mt-1 block w-full border-0 text-left",
                    disabled: exportDisabled.value,
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (exportVisibleAccounts('xlsx')))
                  }, " Excel ", 8, _hoisted_17)
                ])
              ]))
            : _createCommentVNode("", true)
        ], 40, _hoisted_12)
      ]),
      (exportError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(exportError.value), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_19, [
        _withDirectives(_createElementVNode("input", {
          ref_key: "searchInput",
          ref: searchInput,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(searchQuery) ? (searchQuery).value = $event : null)),
          type: "search",
          class: "renewal-control h-11",
          placeholder: "Search account, email or ID",
          "aria-label": "Search renewal accounts"
        }, null, 512), [
          [_vModelText, _unref(searchQuery)]
        ]),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((statusFilter).value = $event)),
          class: "renewal-control h-11",
          "aria-label": "Account status"
        }, [
          _cache[22] || (_cache[22] = _createElementVNode("option", { value: "" }, " All statuses ", -1)),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(accountStatusOptions, (status) => {
            return _createElementVNode("option", {
              key: status,
              value: status
            }, _toDisplayString(status), 9, _hoisted_20)
          }), 64))
        ], 512), [
          [_vModelSelect, statusFilter.value]
        ]),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((attentionFilter).value = $event)),
          class: "renewal-control h-11",
          "aria-label": "Renewal attention"
        }, [...(_cache[23] || (_cache[23] = [
          _createStaticVNode("<option value=\"\" data-v-957c7252> All accounts </option><option value=\"overdueRenewals\" data-v-957c7252> Overdue renewals </option><option value=\"dueThisMonth\" data-v-957c7252> Due this month </option><option value=\"renewalsNextMonth\" data-v-957c7252> Renewals next month </option><option value=\"recentlyRenewed\" data-v-957c7252> Recently renewed </option><option value=\"expiredAccounts\" data-v-957c7252> Expired accounts </option>", 6)
        ]))], 512), [
          [_vModelSelect, attentionFilter.value]
        ]),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((sortBy).value = $event)),
          class: "renewal-control h-11",
          "aria-label": "Sort renewal accounts"
        }, [...(_cache[24] || (_cache[24] = [
          _createElementVNode("option", { value: "account" }, " Sort: account name ", -1),
          _createElementVNode("option", { value: "attention" }, " Sort: attention first ", -1),
          _createElementVNode("option", { value: "payment" }, " Sort: payment due date ", -1),
          _createElementVNode("option", { value: "renewal" }, " Sort: renewal date ", -1)
        ]))], 512), [
          [_vModelSelect, sortBy.value]
        ])
      ]),
      _createElementVNode("div", {
        ref_key: "accountListScroll",
        ref: accountListScroll,
        class: "max-h-[770px] overflow-auto overscroll-contain focus-visible:outline-indigo-500",
        role: "region",
        "aria-label": "Renewal accounts table",
        "aria-busy": _unref(isLoading),
        tabindex: "0"
      }, [
        _createElementVNode("table", _hoisted_22, [
          _cache[29] || (_cache[29] = _createElementVNode("caption", { class: "sr-only" }, " Account renewal dates, status and actions ", -1)),
          _createElementVNode("thead", _hoisted_23, [
            _createElementVNode("tr", null, [
              _cache[25] || (_cache[25] = _createElementVNode("th", {
                scope: "col",
                class: "account-column"
              }, " Account ", -1)),
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(dateColumns, (column) => {
                return _createElementVNode("th", {
                  key: column.field,
                  scope: "col"
                }, _toDisplayString(column.label), 1)
              }), 64)),
              _cache[26] || (_cache[26] = _createElementVNode("th", { scope: "col" }, " Account status ", -1)),
              _cache[27] || (_cache[27] = _createElementVNode("th", {
                scope: "col",
                class: "actions-column"
              }, " Actions ", -1))
            ])
          ]),
          _createElementVNode("tbody", null, [
            (_unref(isLoading))
              ? (_openBlock(), _createElementBlock("tr", _hoisted_24, [...(_cache[28] || (_cache[28] = [
                  _createElementVNode("td", {
                    colspan: "9",
                    class: "h-32 text-center text-[#758299]",
                    role: "status"
                  }, " Loading renewal accounts… ", -1)
                ]))]))
              : (!visibleRows.value.length)
                ? (_openBlock(), _createElementBlock("tr", _hoisted_25, [
                    _createElementVNode("td", _hoisted_26, _toDisplayString(_unref(isError) ? "Account data is unavailable. Use Retry above." : "No accounts match these filters."), 1)
                  ]))
                : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(isLoading) ? [] : paginatedRows.value, ({ client }) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: client.id,
                "data-account-id": client.id
              }, [
                _createElementVNode("td", _hoisted_28, [
                  _createElementVNode("p", _hoisted_29, _toDisplayString(client.companyName || "—"), 1),
                  _createElementVNode("p", _hoisted_30, _toDisplayString(displayEmail(client)), 1)
                ]),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(dateColumns, (column) => {
                  return _createElementVNode("td", {
                    key: column.field,
                    "data-field": column.field,
                    class: "text-[#425a76]"
                  }, _toDisplayString(formatDate(client[column.field])), 9, _hoisted_31)
                }), 64)),
                _createElementVNode("td", _hoisted_32, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold", statusClass(client.clientAccountStatus)])
                  }, _toDisplayString(client.clientAccountStatus), 3)
                ]),
                _createElementVNode("td", _hoisted_33, [
                  _createElementVNode("div", _hoisted_34, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "renewal-button",
                      disabled: preparingEdit.value,
                      "aria-label": 'View details for ' + client.companyName,
                      onClick: ($event: any) => (openDetails(client, $event))
                    }, " View details ", 8, _hoisted_35)
                  ])
                ])
              ], 8, _hoisted_27))
            }), 128))
          ])
        ])
      ], 8, _hoisted_21),
      _createElementVNode("footer", _hoisted_36, [
        _createElementVNode("span", _hoisted_37, "Showing " + _toDisplayString(firstVisibleRow.value) + "–" + _toDisplayString(lastVisibleRow.value) + " of " + _toDisplayString(visibleRows.value.length) + " accounts", 1),
        (pageSize.value !== 'all')
          ? (_openBlock(), _createElementBlock("div", _hoisted_38, [
              _createElementVNode("button", {
                type: "button",
                class: "renewal-button",
                disabled: currentPage.value === 1 || _unref(isLoading),
                onClick: _cache[9] || (_cache[9] = ($event: any) => (currentPage.value--))
              }, " Previous ", 8, _hoisted_39),
              _createElementVNode("span", null, "Page " + _toDisplayString(currentPage.value) + " of " + _toDisplayString(pageCount.value), 1),
              _createElementVNode("button", {
                type: "button",
                class: "renewal-button",
                disabled: currentPage.value === pageCount.value || _unref(isLoading),
                onClick: _cache[10] || (_cache[10] = ($event: any) => (currentPage.value++))
              }, " Next ", 8, _hoisted_40)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    (showDetailsModal.value)
      ? (_openBlock(), _createBlock(_unref(Dialog), {
          key: 3,
          open: showDetailsModal.value,
          "onUpdate:open": _cache[13] || (_cache[13] = ($event: any) => ((showDetailsModal).value = $event))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogContent), {
              class: "max-h-[90vh] w-[calc(100%-2rem)] max-w-6xl overflow-y-auto rounded-[1.5rem] border-0 bg-white p-0 pt-6",
              "aria-describedby": undefined,
              onCloseAutoFocus: closeDetailsFocus
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogTitle), { class: "sr-only" }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Renewal details for " + _toDisplayString(_unref(selectedClient)?.companyName), 1)
                  ]),
                  _: 1
                }),
                _createVNode(RenewalAccountDetails, {
                  client: _unref(selectedClient),
                  "detail-client": _unref(selectedClientDetail),
                  "account-status": selectedAccountStatus.value,
                  "is-loading": _unref(detailIsLoading),
                  "is-error": _unref(detailIsError),
                  "can-edit": canEditRenewalTracking.value,
                  "section-label": "Renewal",
                  onOpenProfile: openFullProfile,
                  onEdit: _cache[11] || (_cache[11] = ($event: any) => (openEditModal(_unref(selectedClient)))),
                  onRetry: _cache[12] || (_cache[12] = ($event: any) => (_unref(refetchDetails)()))
                }, null, 8, ["client", "detail-client", "account-status", "is-loading", "is-error", "can-edit"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["open"]))
      : _createCommentVNode("", true),
    (editingClient.value && canEditRenewalTracking.value)
      ? (_openBlock(), _createBlock(RenewalEditModal, {
          key: 4,
          open: showEditModal.value,
          "onUpdate:open": _cache[14] || (_cache[14] = ($event: any) => ((showEditModal).value = $event)),
          client: editingClient.value,
          "renewal-note": editingNote.value,
          "account-status": editingAccountStatus.value,
          "account-status-options": accountStatusOptions
        }, null, 8, ["open", "client", "renewal-note", "account-status"]))
      : _createCommentVNode("", true)
  ]))
}
}

})